// frontend/src/features/seo/components/ContentStudioModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../../../api/axios';
import landingPagesApi, {
  toApiError as toLandingPageApiError,
  isFeatureDisabled as isLandingPagesDisabled,
} from '../../landingPages/api/landingPagesApi';

/**
 * Landing page CTA placeholder (WF-04). The article stores this token; the
 * server replaces it with the published landing page URL + attribution at
 * publish time. Articles without the token do not need a landing page.
 */
export const LANDING_PAGE_TOKEN = '{{landing_page_url}}';
const LANDING_PAGE_TOKEN_RE = /\{\{\s*landing_page_url\s*\}\}/g;

export function hasLandingPageToken(markdown) {
  return new RegExp(LANDING_PAGE_TOKEN_RE.source).test(markdown || '');
}

const SAFE_PREVIEW_URL = /^https?:\/\/[^\s"'<>()]+$/i;

/** True when the markdown already contains a JSON-LD <script> block. */
export function hasJsonLdSchema(markdown) {
  return /<script type="application\/ld\+json">[\s\S]*?<\/script>/i.test(markdown || '');
}

/** Preview-only substitution so the editor shows where the CTA will link. */
export function substituteLandingPageUrl(markdown, url) {
  const target = url && SAFE_PREVIEW_URL.test(url) ? url : '#landing-page-not-selected';
  return (markdown || '').replace(LANDING_PAGE_TOKEN_RE, target);
}

/**
 * Removes the landing page CTA from the markdown: strips any markdown link that
 * points at the token (e.g. `[Get started today →]({{landing_page_url}})`), then
 * any bare leftover token, and collapses the blank lines the removal leaves
 * behind so the surrounding article spacing stays tidy.
 */
export function removeLandingPageCta(markdown) {
  if (!markdown) return '';
  const linkedCtaRe = /\[[^\]]*\]\(\s*\{\{\s*landing_page_url\s*\}\}\s*\)/g;
  return markdown
    .replace(linkedCtaRe, '')
    .replace(new RegExp(LANDING_PAGE_TOKEN_RE.source, 'g'), '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/\s+$/, '')
    .concat('\n');
}

/**
 * Simple markdown-to-HTML parser for formatted article preview without heavy external deps
 */
function renderMarkdownPreview(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const htmlParts = [];
  let inList = false;
  let inTable = false;
  let inBlockquote = false;
  let blockquoteBuffer = [];
  let inCtaSection = false;
  let ctaTitle = '';
  let ctaBuffer = [];
  let inStatsBlock = false;
  let statsBuffer = [];
  let inGraphBlock = false;
  let graphBuffer = [];

  function flushBlockquote() {
    if (blockquoteBuffer.length === 0) return;
    const rawQuote = blockquoteBuffer.join(' ').trim();
    blockquoteBuffer = [];

    if (
      /^\*\*Direct Answer(?: Definition)?:\*\*/i.test(rawQuote) ||
      /Direct Answer Definition/i.test(rawQuote)
    ) {
      const clean = formatInline(
        rawQuote.replace(/^\*\*Direct Answer(?: Definition)?:\*\*\s*/i, '')
      );
      htmlParts.push(
        `<div class="seo-preview-direct-answer">` +
          `<div class="seo-preview-callout-header"><span class="badge-icon">⚡</span> Direct Answer Definition</div>` +
          `<div class="seo-preview-callout-body">${clean}</div>` +
          `</div>`
      );
      return;
    }

    if (
      /^\*\*Verified (?:Citation|Proof Point)[^*]*\*\*/i.test(rawQuote) ||
      /Verified Citation/i.test(rawQuote)
    ) {
      const clean = formatInline(
        rawQuote.replace(/^\*\*Verified (?:Citation|Proof Point)[^*]*\*\*:\s*/i, '')
      );
      htmlParts.push(
        `<div class="seo-preview-citation">` +
          `<div class="seo-preview-callout-header"><span class="badge-icon">🛡️</span> Verified Grounding Citation (BR-03)</div>` +
          `<div class="seo-preview-callout-body">${clean}</div>` +
          `</div>`
      );
      return;
    }

    if (/^\*\*Key Takeaways?:\*\*/i.test(rawQuote) || /Key Takeaways/i.test(rawQuote)) {
      const clean = formatInline(
        rawQuote.replace(/^\*\*Key Takeaways?:\*\*\s*/i, '').replace(/^[*-]\s+/gm, '• ')
      );
      htmlParts.push(
        `<div class="seo-preview-takeaways">` +
          `<div class="seo-preview-callout-header"><span class="badge-icon">💡</span> Executive Key Takeaways</div>` +
          `<div class="seo-preview-callout-body">${clean}</div>` +
          `</div>`
      );
      return;
    }

    if (
      /^\*\*(?:Pro-Tip|Expert Insight)[^*]*\*\*/i.test(rawQuote) ||
      /(?:Pro-Tip|Expert Insight)/i.test(rawQuote)
    ) {
      const clean = formatInline(
        rawQuote.replace(/^\*\*(?:Pro-Tip|Expert Insight)[^*]*\*\*:\s*/i, '')
      );
      htmlParts.push(
        `<div class="seo-preview-protip">` +
          `<div class="seo-preview-callout-header"><span class="badge-icon">⚡</span> Expert Pro-Tip</div>` +
          `<div class="seo-preview-callout-body">${clean}</div>` +
          `</div>`
      );
      return;
    }

    htmlParts.push(
      `<blockquote class="seo-preview-blockquote"><p>${formatInline(rawQuote)}</p></blockquote>`
    );
  }

  function flushCta() {
    if (!inCtaSection) return;
    const body = formatInline(ctaBuffer.join(' ').trim());
    // When the CTA contains a real link (e.g. the landing page CTA), show that
    // link instead of the placeholder button.
    const hasLink = body.includes('<a ');
    htmlParts.push(
      `<div class="seo-preview-cta-card">` +
        `<h3 class="seo-preview-cta-title">${ctaTitle}</h3>` +
        `<p class="seo-preview-cta-desc">${body}</p>` +
        (hasLink ? '' : `<div class="seo-preview-cta-btn">Get Started Today &rarr;</div>`) +
        `</div>`
    );
    inCtaSection = false;
    ctaTitle = '';
    ctaBuffer = [];
  }

  function flushStats() {
    if (!inStatsBlock || statsBuffer.length === 0) {
      inStatsBlock = false;
      statsBuffer = [];
      return;
    }
    const cards = [];
    for (const raw of statsBuffer) {
      const line = raw.trim();
      if (!line) continue;
      let val = '';
      let lbl = '';
      let sub = '';
      if (line.includes('value:') || line.includes('label:')) {
        const parts = line.split('|');
        for (const part of parts) {
          const [k, ...v] = part.split(':');
          const valStr = v.join(':').trim();
          if (/value/i.test(k)) val = valStr;
          else if (/label/i.test(k)) lbl = valStr;
          else if (/sub/i.test(k)) sub = valStr;
        }
      } else if (line.includes('|')) {
        const parts = line.split('|').map((s) => s.trim());
        val = parts[0] || '';
        lbl = parts[1] || '';
        sub = parts[2] || '';
      }
      if (val || lbl) {
        cards.push({ val: val || '100%', lbl: lbl || 'Metric', sub });
      }
    }

    if (cards.length > 0) {
      const cardHtml = cards
        .map(
          (c) =>
            `<div class="seo-preview-stat-card">` +
            `<div class="seo-preview-stat-number">${formatInline(c.val)}</div>` +
            `<div class="seo-preview-stat-label">${formatInline(c.lbl)}</div>` +
            (c.sub ? `<div class="seo-preview-stat-desc">${formatInline(c.sub)}</div>` : '') +
            `</div>`
        )
        .join('');

      htmlParts.push(`<div class="seo-preview-stats-grid lh-stats-grid">${cardHtml}</div>`);
    }

    inStatsBlock = false;
    statsBuffer = [];
  }

  function flushGraph() {
    if (!inGraphBlock || graphBuffer.length === 0) {
      inGraphBlock = false;
      graphBuffer = [];
      return;
    }
    let graphTitle = 'Performance Benchmark & Growth Analysis';
    const items = [];
    const colors = ['#94a3b8', '#3b82f6', '#10b981', '#8b5cf6', '#f59e0b'];

    for (const raw of graphBuffer) {
      const line = raw.trim();
      if (!line) continue;
      if (line.toLowerCase().startsWith('title:')) {
        graphTitle = line.replace(/^title:\s*/i, '').trim();
      } else if (line.toLowerCase().startsWith('item:')) {
        const parts = line
          .replace(/^item:\s*/i, '')
          .split('|')
          .map((s) => s.trim());
        const label = parts[0] || 'Benchmark';
        const percent = parts[1] || '50%';
        const color = parts[2] || colors[items.length % colors.length];
        items.push({ label, percent, color });
      } else if (line.includes(':') && !line.startsWith('#')) {
        const [lbl, val] = line.split(':').map((s) => s.trim());
        items.push({
          label: lbl,
          percent: val,
          color: colors[items.length % colors.length],
        });
      }
    }

    if (items.length > 0) {
      const barsHtml = items
        .map((it) => {
          const numPercent = Math.min(
            100,
            Math.max(5, parseInt(it.percent.replace(/[^0-9]/g, ''), 10) || 50)
          );
          return (
            `<div class="seo-preview-graph-item">` +
            `<div class="seo-preview-graph-label-row">` +
            `<span>${formatInline(it.label)}</span>` +
            `<span class="seo-preview-graph-percent" style="color: ${it.color};">${it.percent}</span>` +
            `</div>` +
            `<div class="seo-preview-graph-track">` +
            `<div class="seo-preview-graph-bar" style="background: ${it.color}; width: ${numPercent}%;"></div>` +
            `</div>` +
            `</div>`
          );
        })
        .join('');

      htmlParts.push(
        `<div class="seo-preview-graph-card">` +
          `<div class="seo-preview-graph-header"><span class="badge-icon">📊</span> ${formatInline(graphTitle)}</div>` +
          `<div class="seo-preview-graph-bars">${barsHtml}</div>` +
          `</div>`
      );
    }

    inGraphBlock = false;
    graphBuffer = [];
  }

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    // Stats Grid block start/end
    if (trimmed.startsWith(':::stats') || trimmed.startsWith('```stats')) {
      if (inBlockquote) {
        inBlockquote = false;
        flushBlockquote();
      }
      if (inCtaSection) flushCta();
      inStatsBlock = true;
      statsBuffer = [];
      continue;
    }
    if (inStatsBlock) {
      if (trimmed === ':::' || trimmed === '```') {
        flushStats();
      } else {
        statsBuffer.push(trimmed);
      }
      continue;
    }

    // Graph/Chart block start/end
    if (trimmed.startsWith(':::graph') || trimmed.startsWith('```chart')) {
      if (inBlockquote) {
        inBlockquote = false;
        flushBlockquote();
      }
      if (inCtaSection) flushCta();
      inGraphBlock = true;
      graphBuffer = [];
      continue;
    }
    if (inGraphBlock) {
      if (trimmed === ':::' || trimmed === '```') {
        flushGraph();
      } else {
        graphBuffer.push(trimmed);
      }
      continue;
    }

    // Markdown Image detection: ![alt](url "caption") or ![alt](url)
    const imgMatch = trimmed.match(/^!\[(.*?)\]\(([^)\s]+)(?:\s+"(.*?)")?\)$/);
    if (imgMatch) {
      if (inBlockquote) {
        inBlockquote = false;
        flushBlockquote();
      }
      if (inCtaSection) flushCta();
      if (inTable) {
        inTable = false;
        htmlParts.push('</table></div>');
      }
      if (inList) {
        inList = false;
        htmlParts.push('</ul>');
      }
      const alt = formatInline(imgMatch[1] || 'Article image');
      const url = imgMatch[2];
      const caption = imgMatch[3] ? formatInline(imgMatch[3]) : alt;

      htmlParts.push(
        `<div class="seo-preview-image-wrap">` +
          `<img src="${url}" alt="${alt}" class="seo-preview-img" loading="lazy" />` +
          (caption ? `<div class="seo-preview-caption">${caption}</div>` : '') +
          `</div>`
      );
      continue;
    }

    // Table parsing
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      if (inBlockquote) {
        inBlockquote = false;
        flushBlockquote();
      }
      if (inCtaSection) flushCta();

      if (!inTable) {
        inTable = true;
        htmlParts.push('<div class="seo-preview-table-wrap"><table class="seo-preview-table">');
      }
      if (trimmed.includes('---')) {
        continue; // delimiter row
      }
      const cells = trimmed
        .slice(1, -1)
        .split('|')
        .map((c) => c.trim());
      const isHeader = htmlParts[htmlParts.length - 1].includes('<table');
      const tag = isHeader ? 'th' : 'td';
      const row = `<tr>${cells.map((c) => `<${tag}>${formatInline(c)}</${tag}>`).join('')}</tr>`;
      htmlParts.push(row);
      continue;
    } else if (inTable) {
      inTable = false;
      htmlParts.push('</table></div>');
    }

    // List parsing
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      if (inBlockquote) {
        inBlockquote = false;
        flushBlockquote();
      }
      if (inCtaSection) flushCta();

      if (!inList) {
        inList = true;
        htmlParts.push('<ul class="seo-preview-ul">');
      }
      htmlParts.push(`<li>${formatInline(trimmed.slice(2))}</li>`);
      continue;
    } else if (inList) {
      inList = false;
      htmlParts.push('</ul>');
    }

    // Blockquote / Callout
    if (trimmed.startsWith('>')) {
      if (inCtaSection) flushCta();
      inBlockquote = true;
      blockquoteBuffer.push(trimmed.replace(/^>\s*/, ''));
      continue;
    } else if (inBlockquote) {
      inBlockquote = false;
      flushBlockquote();
    }

    // Call to action detection
    if (/^###?\s+(?:Call to Action|Primary Action)/i.test(trimmed)) {
      if (inCtaSection) flushCta();
      inCtaSection = true;
      ctaTitle = trimmed.replace(/^###?\s+/, '');
      continue;
    }

    if (inCtaSection) {
      if (trimmed === '') continue;
      if (trimmed.startsWith('#') || trimmed.startsWith('---')) {
        flushCta();
      } else {
        ctaBuffer.push(trimmed);
        continue;
      }
    }

    // Headings
    if (trimmed.startsWith('# ')) {
      htmlParts.push(`<h1 class="seo-preview-h1">${formatInline(trimmed.slice(2))}</h1>`);
    } else if (trimmed.startsWith('## ')) {
      htmlParts.push(`<h2 class="seo-preview-h2">${formatInline(trimmed.slice(3))}</h2>`);
    } else if (trimmed.startsWith('### ')) {
      htmlParts.push(`<h3 class="seo-preview-h3">${formatInline(trimmed.slice(4))}</h3>`);
    } else if (trimmed.startsWith('---')) {
      htmlParts.push('<hr class="seo-preview-divider" />');
    } else if (trimmed === '') {
      // Empty line
      htmlParts.push('<div class="seo-preview-spacer"></div>');
    } else if (trimmed.startsWith('<div') || trimmed.startsWith('</div>')) {
      htmlParts.push(rawLine);
    } else {
      htmlParts.push(`<p class="seo-preview-p">${formatInline(rawLine)}</p>`);
    }
  }

  if (inTable) htmlParts.push('</table></div>');
  if (inList) htmlParts.push('</ul>');
  if (inBlockquote) flushBlockquote();
  if (inCtaSection) flushCta();
  if (inStatsBlock) flushStats();
  if (inGraphBlock) flushGraph();

  return htmlParts.join('\n');
}

function formatInline(text) {
  if (!text) return '';
  let str = text;
  // Bold
  str = str.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  // Italic
  str = str.replace(/\*(.*?)\*/g, '<em>$1</em>');
  // Code
  str = str.replace(/`(.*?)`/g, '<code>$1</code>');
  // Markdown links [text](url)
  str = str.replace(
    /\[(.*?)\]\((.*?)\)/g,
    '<a href="$2" class="seo-preview-link" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  return str;
}

/**
 * Dynamically builds a contextually relevant hero image markdown from the brief.
 * Prioritizes brief.heroImage, brief.imageUrl, or brief.images, and falls back to
 * category- and entity-aware high-resolution imagery with dynamic alt and caption text.
 */
export function buildDynamicHeroImage(brief) {
  if (!brief) return '';
  const title = brief.title || 'Enterprise B2B Architecture';
  const targetAudience = brief.targetAudience || 'Revenue Leaders';

  let imageUrl = '';
  let caption = '';
  let alt = `${title} - Strategic Architecture`;

  if (brief.heroImage) {
    if (typeof brief.heroImage === 'string') {
      imageUrl = brief.heroImage;
    } else if (typeof brief.heroImage === 'object') {
      imageUrl = brief.heroImage.url || '';
      caption = brief.heroImage.caption || '';
      alt = brief.heroImage.alt || alt;
    }
  } else if (brief.imageUrl) {
    imageUrl = brief.imageUrl;
  } else if (brief.featuredImage) {
    imageUrl = brief.featuredImage;
  } else if (Array.isArray(brief.images) && brief.images.length > 0) {
    const firstImg = brief.images[0];
    imageUrl = typeof firstImg === 'string' ? firstImg : firstImg?.url || '';
  }

  if (!imageUrl) {
    const combinedText =
      `${title} ${(brief.targetEntities || []).join(' ')} ${brief.targetSlug || ''}`.toLowerCase();
    if (
      combinedText.includes('sport') ||
      combinedText.includes('club') ||
      combinedText.includes('fitness') ||
      combinedText.includes('athletic') ||
      combinedText.includes('recreation') ||
      combinedText.includes('gym') ||
      combinedText.includes('league')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1517649763962-0c623266ddc0?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('software') ||
      combinedText.includes('saas') ||
      combinedText.includes("buyer's guide") ||
      combinedText.includes('buyers guide') ||
      combinedText.includes('management') ||
      combinedText.includes('platform') ||
      combinedText.includes('tool') ||
      combinedText.includes('system')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('email') ||
      combinedText.includes('deliverability') ||
      combinedText.includes('outbound') ||
      combinedText.includes('inbox') ||
      combinedText.includes('cadence')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('data') ||
      combinedText.includes('enrichment') ||
      combinedText.includes('analytics') ||
      combinedText.includes('firmographic') ||
      combinedText.includes('waterfall')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('ai') ||
      combinedText.includes('aieo') ||
      combinedText.includes('agent') ||
      combinedText.includes('llm') ||
      combinedText.includes('gpt') ||
      combinedText.includes('model') ||
      combinedText.includes('search')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('security') ||
      combinedText.includes('compliance') ||
      combinedText.includes('privacy') ||
      combinedText.includes('governance') ||
      combinedText.includes('shield')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1200&q=80';
    } else if (
      combinedText.includes('sdr') ||
      combinedText.includes('pipeline') ||
      combinedText.includes('revenue') ||
      combinedText.includes('growth') ||
      combinedText.includes('conversion') ||
      combinedText.includes('lead')
    ) {
      imageUrl =
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80';
    } else {
      const fallbackPool = [
        'https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=1200&q=80',
        'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
      ];
      const charSum = title.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
      imageUrl = fallbackPool[charSum % fallbackPool.length];
    }
  }

  if (!caption) {
    const primaryEntity = (brief.targetEntities || [])[0];
    caption = primaryEntity
      ? `${title} - ${primaryEntity} Implementation Framework for ${targetAudience}`
      : `${title} - Strategic Framework for ${targetAudience}`;
  }

  return `![${alt}](${imageUrl} "${caption}")\n\n`;
}

/**
 * Dynamically builds a contextually relevant section diagram or dashboard image.
 */
export function buildDynamicSectionImage(brief, sectionHeading = '', index = 1) {
  if (!brief) return '';
  const cleanHeading = sectionHeading
    ? sectionHeading.replace(/^[0-9.#\s]+/, '').trim()
    : 'System Architecture & Data Workflow';

  if (Array.isArray(brief.sectionImages) && brief.sectionImages[index]) {
    const item = brief.sectionImages[index];
    if (typeof item === 'string') {
      return `![${cleanHeading}](${item} "${cleanHeading} - Architecture & Operations")\n\n`;
    }
    return `![${item.alt || cleanHeading}](${item.url} "${item.caption || cleanHeading}")\n\n`;
  }
  if (Array.isArray(brief.images) && brief.images.length > index) {
    const item = brief.images[index];
    const url = typeof item === 'string' ? item : item?.url;
    if (url) {
      return `![${cleanHeading}](${url} "${cleanHeading} - Visual Architecture")\n\n`;
    }
  }

  const combined =
    `${cleanHeading} ${brief?.title || ''} ${(brief?.targetEntities || []).join(' ')}`.toLowerCase();
  let url =
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80';

  if (
    combined.includes('sport') ||
    combined.includes('club') ||
    combined.includes('facility') ||
    combined.includes('schedule')
  ) {
    url =
      'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1200&q=80';
  } else if (
    combined.includes('software') ||
    combined.includes('dashboard') ||
    combined.includes('platform') ||
    combined.includes('ui')
  ) {
    url =
      'https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=1200&q=80';
  } else if (
    combined.includes('flow') ||
    combined.includes('sequence') ||
    combined.includes('cadence') ||
    combined.includes('email')
  ) {
    url =
      'https://images.unsplash.com/photo-1557200134-90327ee9fafa?auto=format&fit=crop&w=1200&q=80';
  } else if (
    combined.includes('ai') ||
    combined.includes('model') ||
    combined.includes('neural') ||
    combined.includes('answer engine') ||
    combined.includes('intelligence')
  ) {
    url =
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80';
  } else if (
    combined.includes('team') ||
    combined.includes('organization') ||
    combined.includes('governance') ||
    combined.includes('people') ||
    combined.includes('leader')
  ) {
    url =
      'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80';
  } else if (
    combined.includes('result') ||
    combined.includes('metric') ||
    combined.includes('benchmark') ||
    combined.includes('roi') ||
    combined.includes('growth')
  ) {
    url =
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80';
  } else {
    const pool = [
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1507679799987-c73779587ccf?auto=format&fit=crop&w=1200&q=80',
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=1200&q=80',
    ];
    url = pool[index % pool.length];
  }

  return `![${cleanHeading}](${url} "${cleanHeading} - Operational Architecture & Intelligence")\n\n`;
}

/**
 * Dynamically builds a 3-metric KPI stats grid (lh-stats-grid) tailored to the brief.
 * Prioritizes brief.stats / brief.statsGrid, parses figures from brief.proofPoints,
 * and contextualizes metrics around brief entities, title domain, and audience.
 */
export function buildDynamicStatsGrid(brief) {
  if (!brief) return '';

  const customStats = Array.isArray(brief.stats)
    ? brief.stats
    : Array.isArray(brief.statsGrid)
      ? brief.statsGrid
      : Array.isArray(brief.metrics)
        ? brief.metrics
        : null;

  if (customStats && customStats.length > 0) {
    const lines = customStats.map((item) => {
      const v = item.value || item.val || '100%';
      const l = item.label || item.lbl || 'Metric';
      const s = item.sub || item.desc || '';
      return `value: ${v} | label: ${l} | sub: ${s}`;
    });
    return `:::stats\n${lines.join('\n')}\n:::\n\n`;
  }

  const title = (brief.title || '').toLowerCase();
  const entities = Array.isArray(brief.targetEntities) ? brief.targetEntities : [];
  const primaryEntity = entities[0] || '';
  const secondaryEntity = entities[1] || '';
  const proofPoints = Array.isArray(brief.proofPoints) ? brief.proofPoints : [];

  const cards = [];

  // Extract quantitative proof points if available
  for (const pp of proofPoints) {
    if (cards.length >= 2) break;
    const match = pp.match(/([+<>]?\s*\d+(?:\.\d+)?%|[+<>]?\s*\d+(?:\.\d+)?x)/i);
    if (match) {
      const val = match[1].replace(/\s+/g, '');
      let cleanLabel = pp
        .replace(/([+<>]?\s*\d+(?:\.\d+)?%|[+<>]?\s*\d+(?:\.\d+)?x)/gi, '')
        .replace(/^(?:grounded insight|validated against|articles backed by):\s*/i, '')
        .replace(/[.,:;]+$/, '')
        .trim();
      if (cleanLabel.length > 28) {
        cleanLabel = cleanLabel.split(' ').slice(0, 3).join(' ');
      }
      cards.push({
        value:
          val.startsWith('+') || val.includes('%') || val.includes('x') || val.startsWith('<')
            ? val
            : `+${val}`,
        label: cleanLabel || (primaryEntity ? `${primaryEntity} Benchmark` : 'Verified Citation'),
        sub: pp.length > 80 ? `${pp.slice(0, 77)}...` : pp,
      });
    }
  }

  // Domain-specific metric defaults
  if (
    title.includes('sport') ||
    title.includes('club') ||
    title.includes('fitness') ||
    title.includes('athletic') ||
    title.includes('recreation') ||
    title.includes('gym')
  ) {
    if (cards.length < 1) {
      cards.push({
        value: '+42%',
        label: 'Member Retention Rate',
        sub: 'Automated renewal & membership engagement benchmark',
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '-65%',
        label: 'Admin Overhead',
        sub: 'Reduction in manual registration & roster management hours',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '94%',
        label: 'Payment Collection Velocity',
        sub: 'Digital dues & facility booking collection efficiency',
      });
    }
  } else if (
    title.includes('email') ||
    title.includes('deliverability') ||
    title.includes('inbox') ||
    title.includes('outbound')
  ) {
    if (cards.length < 1) {
      cards.push({
        value: '99.4%',
        label: primaryEntity ? `${primaryEntity} Placement` : 'Inbox Placement Rate',
        sub: 'Verified sender reputation benchmark across enterprise mailboxes',
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '< 0.5%',
        label: 'Hard Bounce Standard',
        sub: 'Zero-bounce deliverability threshold for verified contact data',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '3.6x',
        label: 'Qualified Reply Velocity',
        sub: 'Positive response multiplier compared to generic outreach templates',
      });
    }
  } else if (
    title.includes('enrich') ||
    title.includes('data') ||
    title.includes('contact') ||
    title.includes('waterfall')
  ) {
    if (cards.length < 1) {
      cards.push({
        value: '+185%',
        label: primaryEntity ? `${primaryEntity} Match Rate` : 'Waterfall Verification Match Rate',
        sub: 'Multi-vendor waterfall verification coverage across commercial records',
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '94%',
        label: secondaryEntity ? `${secondaryEntity} Accuracy` : 'Verified Entity Grounding',
        sub: 'Live email and direct dial validation accuracy at execution time',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '2.9x',
        label: 'Account Engagement Velocity',
        sub: 'Speed to first qualified commercial touchpoint with buying committee',
      });
    }
  } else if (
    title.includes('sdr') ||
    title.includes('agent') ||
    title.includes('cadence') ||
    title.includes('autonomous')
  ) {
    if (cards.length < 1) {
      cards.push({
        value: '3.4x',
        label: 'Meeting Conversion Rate',
        sub: 'Autonomous intent qualification vs manual SDR baseline',
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '-68%',
        label: 'Cost Per Opportunity',
        sub: 'Reduction in outbound customer acquisition expenditures',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '91%',
        label: 'Buyer Intent Precision',
        sub: 'Signal-based timing alignment for high-propensity enterprise leads',
      });
    }
  } else if (
    title.includes('software') ||
    title.includes('saas') ||
    title.includes("buyer's guide") ||
    title.includes('buyers guide') ||
    title.includes('platform') ||
    title.includes('management')
  ) {
    if (cards.length < 1) {
      cards.push({
        value: '+38%',
        label: 'Operational Efficiency',
        sub: 'Process automation & centralized administration benchmark',
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '-52%',
        label: 'Time-to-Value',
        sub: 'Accelerated deployment & onboarding velocity vs legacy tools',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '91%',
        label: 'User Adoption Rate',
        sub: 'Staff & stakeholder self-service engagement standard',
      });
    }
  } else {
    // General B2B / AIEO baseline contextualized with entities
    if (cards.length < 1) {
      cards.push({
        value: '+142%',
        label: primaryEntity ? `${primaryEntity} Growth` : 'Intent Pipeline Growth',
        sub: `Year-over-year commercial velocity for ${brief.targetAudience || 'B2B teams'}`,
      });
    }
    if (cards.length < 2) {
      cards.push({
        value: '84%',
        label: secondaryEntity ? `${secondaryEntity} Visibility` : 'AI Engine Visibility',
        sub: 'Citation benchmark across ChatGPT, Claude & Perplexity answer engines',
      });
    }
    if (cards.length < 3) {
      cards.push({
        value: '3.8x',
        label: 'Conversion Velocity',
        sub: 'Commercial conversion multiplier vs ungrounded legacy content',
      });
    }
  }

  const statLines = cards
    .slice(0, 3)
    .map((c) => `value: ${c.value} | label: ${c.label} | sub: ${c.sub}`);
  return `:::stats\n${statLines.join('\n')}\n:::\n\n`;
}

/**
 * Dynamically builds a visual comparison graph tailored to the brief.
 * Prioritizes brief.graph / brief.chart and dynamically configures benchmark bars.
 */
export function buildDynamicGraph(brief) {
  if (!brief) return '';

  if (brief.graph && brief.graph.items && Array.isArray(brief.graph.items)) {
    const title = brief.graph.title || `${brief.title || 'Performance'} Benchmark Analysis`;
    const lines = brief.graph.items.map(
      (it) => `item: ${it.label} | ${it.percent || it.value} | ${it.color || '#3b82f6'}`
    );
    return `:::graph:bar\ntitle: ${title}\n${lines.join('\n')}\n:::\n\n`;
  }

  const rawTitle = brief.title || 'Market Benchmark';
  const cleanTitle = rawTitle
    .replace(/^#+\s*/, '')
    .replace(/^(?:the\s+)?(?:\d{4}\s+)?(?:strategic\s+|definitive\s+)?guide\s+to\s+/i, '')
    .trim();

  const entities = Array.isArray(brief.targetEntities) ? brief.targetEntities : [];
  const primaryEntity = entities[0] || 'Optimized Strategy';
  const secondaryEntity = entities[1] || 'Schema & Entities';

  const titleLower = rawTitle.toLowerCase();
  let graphTitle = `${cleanTitle} vs Industry Standard (2026 Index)`;
  let baselineLabel = 'Traditional SEO Baseline';
  let intermediateLabel =
    secondaryEntity.length > 25 ? 'Schema-Enriched Entities' : `${secondaryEntity} Adoption`;
  let optimizedLabel = `Lead Huntrix ${primaryEntity.length > 20 ? 'AIEO Optimized' : primaryEntity} Engine`;

  let baselinePercent = '28%';
  let intermediatePercent = '64%';
  let optimizedPercent = '94%';

  if (
    titleLower.includes('sport') ||
    titleLower.includes('club') ||
    titleLower.includes('fitness') ||
    titleLower.includes('athletic') ||
    titleLower.includes('recreation')
  ) {
    graphTitle = `${cleanTitle} Operational Efficiency (2026 Index)`;
    baselineLabel = 'Manual Spreadsheets & Paper Forms';
    intermediateLabel = 'Disjointed Point Solutions';
    optimizedLabel = 'Unified Club Management Platform';
    baselinePercent = '22%';
    intermediatePercent = '58%';
    optimizedPercent = '95%';
  } else if (titleLower.includes('email') || titleLower.includes('deliverability')) {
    baselineLabel = 'Shared IP / Unwarmed Domain';
    intermediateLabel = 'Standard SPF/DKIM Configuration';
    optimizedLabel = 'Lead Huntrix AI-Warmed & Verified';
    baselinePercent = '32%';
    intermediatePercent = '71%';
    optimizedPercent = '99%';
  } else if (titleLower.includes('enrich') || titleLower.includes('data')) {
    baselineLabel = 'Single-Provider Static Database';
    intermediateLabel = 'Standard CSV Enrichment';
    optimizedLabel = 'Lead Huntrix Waterfall Verification';
    baselinePercent = '38%';
    intermediatePercent = '65%';
    optimizedPercent = '96%';
  } else if (titleLower.includes('sdr') || titleLower.includes('agent')) {
    baselineLabel = 'Manual Rep Prospecting';
    intermediateLabel = 'Generic Sequence Automation';
    optimizedLabel = 'Lead Huntrix Autonomous AI SDR';
    baselinePercent = '24%';
    intermediatePercent = '58%';
    optimizedPercent = '93%';
  } else if (
    titleLower.includes('software') ||
    titleLower.includes('saas') ||
    titleLower.includes("buyer's guide") ||
    titleLower.includes('buyers guide') ||
    titleLower.includes('platform') ||
    titleLower.includes('management')
  ) {
    graphTitle = `${cleanTitle} Solution Maturity (2026 Index)`;
    baselineLabel = 'Legacy Manual Workflows';
    intermediateLabel = 'Fragmented Point Tools';
    optimizedLabel = 'Integrated Modern Platform';
    baselinePercent = '25%';
    intermediatePercent = '61%';
    optimizedPercent = '94%';
  }

  return (
    `:::graph:bar\n` +
    `title: ${graphTitle}\n` +
    `item: ${baselineLabel} | ${baselinePercent} | #94a3b8\n` +
    `item: ${intermediateLabel} | ${intermediatePercent} | #3b82f6\n` +
    `item: ${optimizedLabel} | ${optimizedPercent} | #10b981\n` +
    `:::\n\n`
  );
}

/**
 * Dynamically builds a comparison table tailored to the brief domain.
 */
export function buildDynamicComparisonTable(brief) {
  if (!brief) return '';
  const title = (brief.title || '').toLowerCase();

  if (
    title.includes('sport') ||
    title.includes('club') ||
    title.includes('fitness') ||
    title.includes('athletic') ||
    title.includes('recreation')
  ) {
    return (
      `| Core Capability | Legacy / Manual Process | Modern Club Platform |\n` +
      `| --- | --- | --- |\n` +
      `| Member & Roster Registration | Fragmented paper forms & manual spreadsheet entry | Automated online self-service & digital waiver signing |\n` +
      `| Facility & Court Scheduling | Manual conflict resolution & accidental double-bookings | Real-time synchronized calendars & automated member booking |\n` +
      `| Dues & Payment Processing | Delayed check deposits & manual reconciliation | Automated recurring billing, digital invoices & instant payouts |\n` +
      `| Attendance & Retention Analytics | Disconnected logs with high member churn | Real-time dashboards, attendance tracking & renewal alerts |\n\n`
    );
  }

  if (
    title.includes('software') ||
    title.includes('saas') ||
    title.includes("buyer's guide") ||
    title.includes('buyers guide') ||
    title.includes('platform') ||
    title.includes('management')
  ) {
    return (
      `| Evaluation Criteria | Legacy / Manual Methods | Modern Specialized Platform |\n` +
      `| --- | --- | --- |\n` +
      `| Operational Workflows | Siloed spreadsheets & error-prone manual handoffs | Centralized automated workflows & role-based permissions |\n` +
      `| Stakeholder Onboarding | Steep learning curve & low cross-team adoption | Intuitive self-service interface & guided deployment |\n` +
      `| Data Security & Compliance | Vulnerable local files & lack of audit trails | Enterprise encryption, automated backups & role governance |\n` +
      `| Reporting & Insights | Delayed monthly reports with incomplete visibility | Real-time operational dashboards & predictive analytics |\n\n`
    );
  }

  return (
    `| Capability | Legacy Search Playbook | Lead Huntrix AIEO Framework |\n` +
    `| --- | --- | --- |\n` +
    `| Target Audience | Search Engine Spiders | Generative AI Models & Buying Committees |\n` +
    `| Optimization Focus | Exact-match Keyword Density | Semantic Knowledge Graph & Grounded Claims |\n` +
    `| Inbound Conversion | High Bounce / Generic Traffic | High Intent / Verified Pipeline Opportunities |\n\n`
  );
}

/**
 * Dynamically builds a Direct Answer 60-word definition.
 */
export function buildDynamicDirectAnswer(brief) {
  if (!brief) return '';
  const title = (brief.title || '').trim();
  const audience = brief.targetAudience || 'decision-makers and operations teams';
  const titleLower = title.toLowerCase();

  if (
    titleLower.includes('sport') ||
    titleLower.includes('club') ||
    titleLower.includes('fitness') ||
    titleLower.includes('athletic') ||
    titleLower.includes('recreation')
  ) {
    return `> **Direct Answer Definition:** ${title} provides sports organizations, club directors, and athletic staff with a centralized software platform to automate member registration, court/facility scheduling, recurring dues billing, and team communications from a single digital dashboard.\n\n`;
  }

  if (
    titleLower.includes('software') ||
    titleLower.includes('saas') ||
    titleLower.includes("buyer's guide") ||
    titleLower.includes('buyers guide') ||
    titleLower.includes('platform') ||
    titleLower.includes('management')
  ) {
    return `> **Direct Answer Definition:** ${title} delivers an objective, capability-driven evaluation framework enabling ${audience} to assess core features, total cost of ownership, integration capabilities, and deployment velocity to select the optimal solution.\n\n`;
  }

  return `> **Direct Answer Definition:** ${title} represents the strategic operational framework used by modern ${audience} to capture, enrich, and convert verified commercial demand across search and AI answer engines.\n\n`;
}

/**
 * Dynamically builds Executive Key Takeaways tailored to the brief.
 */
export function buildDynamicKeyTakeaways(brief) {
  if (!brief) return '';
  const title = (brief.title || '').toLowerCase();

  if (
    title.includes('sport') ||
    title.includes('club') ||
    title.includes('fitness') ||
    title.includes('athletic') ||
    title.includes('recreation')
  ) {
    return (
      `> **Key Takeaways:**\n` +
      `> - **Administrative Automation:** Modern sports club management systems eliminate up to 15 hours of weekly manual paperwork through self-service registration and digital waivers.\n` +
      `> - **Frictionless Dues Collection:** Automated recurring billing and online payments reduce delinquencies and revenue leakage by over 80%.\n` +
      `> - **Real-Time Facility Scheduling:** Centralized resource booking prevents double-bookings and gives coaches, parents, and athletes immediate mobile visibility.\n\n`
    );
  }

  if (
    title.includes('software') ||
    title.includes('saas') ||
    title.includes("buyer's guide") ||
    title.includes('buyers guide') ||
    title.includes('platform') ||
    title.includes('management')
  ) {
    return (
      `> **Key Takeaways:**\n` +
      `> - **Consolidated Architecture:** Modern software replaces fragmented point tools with a single unified system of record, slashing total cost of ownership.\n` +
      `> - **Time-to-Value Acceleration:** Pre-built workflow templates and self-service administration reduce onboarding time from months to days.\n` +
      `> - **Auditable Governance:** Enterprise security, role-based access, and real-time reporting ensure seamless organizational compliance.\n\n`
    );
  }

  return (
    `> **Key Takeaways:**\n` +
    `> - **AI Engine Optimization (AIEO):** Generative answer engines synthesize structured entities rather than indexing raw keywords.\n` +
    `> - **Verified Grounding:** Articles backed by quantifiable citations achieve 3.8x higher citation frequency in ChatGPT and Claude.\n` +
    `> - **Full-Funnel Alignment:** Contextual internal links guide verified inbound buyers directly to conversion milestones.\n\n`
  );
}

/**
 * Dynamically builds an Expert Pro-Tip callout tailored to the brief.
 */
export function buildDynamicProTip(brief) {
  if (!brief) return '';
  const title = (brief.title || '').toLowerCase();

  if (
    title.includes('sport') ||
    title.includes('club') ||
    title.includes('fitness') ||
    title.includes('athletic') ||
    title.includes('recreation')
  ) {
    return `> **Pro-Tip:** Prioritize club management platforms offering native mobile self-service apps and automated payment reminders, which directly correlate with higher season-over-season member renewal rates.\n\n`;
  }

  if (
    title.includes('software') ||
    title.includes('saas') ||
    title.includes("buyer's guide") ||
    title.includes('buyers guide') ||
    title.includes('platform') ||
    title.includes('management')
  ) {
    return `> **Pro-Tip:** During vendor evaluation, require a live sandbox trial using your team's real operational workflows rather than relying solely on pre-recorded demo presentations.\n\n`;
  }

  return `> **Pro-Tip:** Structure critical definitions and benchmark statistics within 60 words directly beneath major H2 headings to maximize Google AI Overviews and Perplexity extraction.\n\n`;
}

export default function ContentStudioModal({
  isOpen,
  onClose,
  brief,
  onSaveDraft,
  onAuditDraft,
  onPublishToCms,
  governanceSettings = {},
  onSaveGovernance,
  onOpenGovernance,
  onRequestApproveBrief,
  scrollToSchema = false,
  onLinkLandingPage,
  onOpenLandingPages = () => window.location.assign('/landing-pages'),
  onStartSchemaFlow,
  hasAudit = false,
}) {
  const [viewMode, setViewMode] = useState('split'); // 'split' | 'editor' | 'preview'
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [isDrafting, setIsDrafting] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved' | 'unsaved' | 'saving'
  const [copyToast, setCopyToast] = useState(null);
  const [isInspectorOpen, setIsInspectorOpen] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [publishedData, setPublishedData] = useState(null);
  const [quickCmsEndpoint, setQuickCmsEndpoint] = useState('');
  const [quickCmsProvider, setQuickCmsProvider] = useState('WordPress');
  const [isSearchingImage, setIsSearchingImage] = useState(false);
  const [foundImagesList, setFoundImagesList] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  // Landing page CTA (published landing pages in the active workspace)
  const [landingPages, setLandingPages] = useState([]);
  const [landingPagesStatus, setLandingPagesStatus] = useState('idle'); // idle|loading|ready|disabled|error
  const [linkedLandingPageId, setLinkedLandingPageId] = useState(null);
  const [isLinkingLandingPage, setIsLinkingLandingPage] = useState(false);
  const [showLandingPageNotice, setShowLandingPageNotice] = useState(false);
  const [publishErrorCode, setPublishErrorCode] = useState(null);
  const [showSchemaPrompt, setShowSchemaPrompt] = useState(false);
  const [schemaAuditHint, setSchemaAuditHint] = useState(false);
  const textareaRef = useRef(null);

  const handleConnectDemoCms = () => {
    const updated = {
      ...governanceSettings,
      cmsProvider: 'WordPress',
      cmsEndpoint: 'https://staging.example.com/wp-json/',
      connectionStatus: 'connected',
    };
    if (onSaveGovernance) {
      onSaveGovernance(updated);
    }
  };

  const handleSaveQuickEndpoint = () => {
    if (!quickCmsEndpoint.trim()) return;
    const updated = {
      ...governanceSettings,
      cmsProvider: quickCmsProvider || governanceSettings.cmsProvider || 'WordPress',
      cmsEndpoint: quickCmsEndpoint.trim(),
      connectionStatus: 'connected',
    };
    if (onSaveGovernance) {
      onSaveGovernance(updated);
    }
  };

  // Initialize draft content and publishing provenance when modal opens or brief changes
  useEffect(() => {
    if (!brief) return;

    if (brief.draftArticle?.markdown) {
      setDraftMarkdown(brief.draftArticle.markdown);
      setSaveStatus('saved');
    } else if (brief.content) {
      setDraftMarkdown(brief.content);
      setSaveStatus('saved');
    } else {
      // Dynamic initial scaffolding with topic-aware hero image, stats grid, comparison graph, and outline
      const heroImageMd = buildDynamicHeroImage(brief);
      const directAnswerMd = buildDynamicDirectAnswer(brief);
      const takeawaysMd = buildDynamicKeyTakeaways(brief);
      const statsMd = buildDynamicStatsGrid(brief);
      const graphMd = buildDynamicGraph(brief);
      const workflowImgMd = buildDynamicSectionImage(
        brief,
        'Core Implementation Architecture & Operations',
        1
      );
      const protipMd = buildDynamicProTip(brief);

      const outlineMd =
        Array.isArray(brief.outline) && brief.outline.length > 0
          ? brief.outline
              .map((o, idx) => {
                const hashes = o.level === 'H1' ? '#' : o.level === 'H2' ? '##' : '###';
                let extra = '';
                if (idx === 1) extra = `\n\n${workflowImgMd}`;
                if (idx === 2) extra = `\n\n${graphMd}`;
                return `${hashes} ${o.text}\n\nComprehensive analysis and implementation guidelines for ${o.text}.${extra}`;
              })
              .join('\n\n')
          : `## 1. Strategic Market Context & Overview\n\nComprehensive market analysis and operational foundation.\n\n## 2. Core Implementation Architecture\n\n${workflowImgMd}Step-by-step implementation guide.\n\n## 3. Measurable Benchmarks & Growth Results\n\n${graphMd}Performance metrics and verified return on investment.`;

      const proofMd =
        Array.isArray(brief.proofPoints) && brief.proofPoints.length > 0
          ? `\n\n> **Verified Citation (BR-03):** ${brief.proofPoints[0]}`
          : '';
      const ctaMd = brief.cta
        ? `\n\n---\n\n### Primary Action\n\n${brief.cta}`
        : `\n\n---\n\n### Primary Action\n\nAccelerate your revenue engine with Lead Huntrix verified B2B intelligence and automated answer engine optimization.`;

      const initialScaffold = `# ${brief.title}\n\n${heroImageMd}${directAnswerMd}${takeawaysMd}${statsMd}${outlineMd}${proofMd}\n\n${protipMd}${ctaMd}`;
      setDraftMarkdown(initialScaffold);
      setSaveStatus('unsaved');
    }

    if (brief.draftArticle?.publishedAt) {
      setPublishedData({
        cmsPostId: brief.draftArticle.cmsPostId,
        cmsPostUrl: brief.draftArticle.cmsPostUrl,
        cmsStatus: brief.draftArticle.cmsStatus,
        publishedAt: brief.draftArticle.publishedAt,
        cmsProvider: brief.draftArticle.cmsProvider,
      });
    } else {
      setPublishedData(null);
    }
    setPublishError(null);
  }, [brief]);

  // Landing page link follows the brief (separate from draft init so linking a
  // page never resets unsaved article edits).
  useEffect(() => {
    setLinkedLandingPageId(brief?.landingPageId || null);
    setShowLandingPageNotice(false);
  }, [brief?.id, brief?.landingPageId]);

  // Load published landing pages for the CTA picker.
  useEffect(() => {
    if (!isOpen || !brief?.id) return undefined;
    let cancelled = false;
    setLandingPagesStatus('loading');
    (async () => {
      try {
        const data = await landingPagesApi.list(undefined, { limit: 100 });
        if (cancelled) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        // Approved pages only; unpublished ones are shown disabled so the user
        // knows they still need to be published in the builder.
        setLandingPages(items.filter((p) => p.approvedVersionId));
        setLandingPagesStatus('ready');
      } catch (err) {
        if (cancelled) return;
        setLandingPages([]);
        setLandingPagesStatus(
          isLandingPagesDisabled(toLandingPageApiError(err)) ? 'disabled' : 'error'
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, brief?.id]);

  // Auto-scroll to JSON-LD schema in editor pane when requested
  useEffect(() => {
    if (isOpen && scrollToSchema && textareaRef.current) {
      const timer = setTimeout(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        const currentText = textarea.value || draftMarkdown || '';
        const schemaIdx = currentText.indexOf('<script type="application/ld+json">');

        if (schemaIdx !== -1) {
          const textBefore = currentText.substring(0, schemaIdx);
          const linesBefore = textBefore.split('\n').length;
          const totalLines = currentText.split('\n').length || 1;
          const scrollFraction = linesBefore / totalLines;
          const targetScrollTop = scrollFraction * textarea.scrollHeight;
          textarea.scrollTop = Math.max(0, targetScrollTop - 40);

          try {
            textarea.focus();
            textarea.setSelectionRange(schemaIdx, schemaIdx);
          } catch (e) {
            // Ignore selection range issues in testing or non-standard environments
          }
        } else {
          textarea.scrollTop = textarea.scrollHeight;
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, scrollToSchema, draftMarkdown]);

  // Word count & Read time
  const wordCount = useMemo(() => {
    if (!draftMarkdown) return 0;
    return draftMarkdown.split(/\s+/).filter(Boolean).length;
  }, [draftMarkdown]);

  const targetWordCount = brief?.targetWordCount || 2200;
  const wordCountPercent = Math.min(100, Math.round((wordCount / targetWordCount) * 100));
  const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));

  // Entity coverage scan
  const entityCoverage = useMemo(() => {
    const entities = Array.isArray(brief?.targetEntities) ? brief.targetEntities : [];
    if (!entities.length) return [];

    const lowerText = (draftMarkdown || '').toLowerCase();
    return entities.map((ent) => {
      const escaped = ent.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
      const matches = lowerText.match(regex);
      const count = matches ? matches.length : 0;
      return {
        entity: ent,
        present: count > 0,
        occurrences: count,
      };
    });
  }, [brief?.targetEntities, draftMarkdown]);

  // Proof points check
  const proofPointStatus = useMemo(() => {
    const points = Array.isArray(brief?.proofPoints) ? brief.proofPoints : [];
    const lowerText = (draftMarkdown || '').toLowerCase();
    return points.map((p) => {
      const snippet = p.slice(0, 30).toLowerCase();
      const present = lowerText.includes(snippet);
      return { point: p, present };
    });
  }, [brief?.proofPoints, draftMarkdown]);

  // Internal links check
  const internalLinkStatus = useMemo(() => {
    const links = Array.isArray(brief?.internalLinks) ? brief.internalLinks : [];
    const lowerText = (draftMarkdown || '').toLowerCase();
    return links.map((l) => {
      const pageMatch = lowerText.includes(l.page.toLowerCase());
      const anchorMatch = lowerText.includes(l.anchorText.toLowerCase());
      return {
        ...l,
        present: pageMatch || anchorMatch,
      };
    });
  }, [brief?.internalLinks, draftMarkdown]);

  // CTA check
  const ctaCheck = useMemo(() => {
    if (!brief?.cta) return { present: false, cta: '' };
    const snippet = brief.cta.slice(0, 25).toLowerCase();
    return {
      present: (draftMarkdown || '').toLowerCase().includes(snippet),
      cta: brief.cta,
    };
  }, [brief?.cta, draftMarkdown]);

  const handleTextChange = (e) => {
    setDraftMarkdown(e.target.value);
    setSaveStatus('unsaved');
  };

  // Generate full article using Gemini
  const handleSynthesizeArticle = async () => {
    if (!brief) return;
    setIsDrafting(true);

    // Preserve any JSON-LD schema block already present in the current draft so a
    // full re-draft does not discard structured data injected from the Schema Studio.
    const schemaBlockRegex = /<script type="application\/ld\+json">[\s\S]*?<\/script>/gi;
    const existingSchemaBlocks = (draftMarkdown || '').match(schemaBlockRegex) || [];
    const preservedSchema =
      existingSchemaBlocks.length > 0 ? `\n\n${existingSchemaBlocks.join('\n\n')}\n` : '';
    // Avoid duplicating the schema if the newly generated draft already contains it.
    const appendSchema = (markdown) => {
      if (!preservedSchema) return markdown;
      schemaBlockRegex.lastIndex = 0;
      if (schemaBlockRegex.test(markdown || '')) return markdown;
      return `${(markdown || '').replace(/\s+$/, '')}${preservedSchema}`;
    };

    try {
      const response = await axiosInstance.post('/seo/briefs/draft', { brief });
      if (response.data && response.data.success && response.data.data) {
        const article = response.data.data;
        setDraftMarkdown(appendSchema(article.markdown));
        setSaveStatus('unsaved');
        setCopyToast(
          preservedSchema
            ? 'AI drafted full article (JSON-LD schema preserved)!'
            : 'AI drafted full article based on brief!'
        );
        setTimeout(() => setCopyToast(null), 3000);
        return;
      }
    } catch (err) {
      console.warn(
        '[ContentStudioModal] AI drafting failed, using heuristic synthesizer:',
        err.message
      );
      // Fallback synthesizer with dynamic imagery, graphs, statistics, and decorations
      const heroImage = buildDynamicHeroImage(brief);
      const directAnswer = buildDynamicDirectAnswer(brief);
      const keyTakeaways = buildDynamicKeyTakeaways(brief);
      const statsGrid = buildDynamicStatsGrid(brief);
      const comparisonGraph = buildDynamicGraph(brief);
      const proTipCallout = buildDynamicProTip(brief);
      const sectionImage = buildDynamicSectionImage(
        brief,
        'Operational Architecture and Implementation Workflow',
        2
      );
      const comparisonTable = buildDynamicComparisonTable(brief);

      const outlineSections = (brief.outline || [])
        .filter((o) => o.level !== 'H1')
        .map((item, idx) => {
          const hashes = item.level === 'H2' ? '##' : '###';
          const cleanHeading = item.text.replace(/^[0-9.]+\s*/, '');
          const ent = (brief.targetEntities || [])[idx] || cleanHeading;
          const proof = (brief.proofPoints || [])[idx % (brief.proofPoints?.length || 1)];

          let sectionBody = `When evaluating and implementing ${cleanHeading}, organizations and ${brief.targetAudience || 'teams'} must align **${ent}** to optimize operational efficiency and performance.\n\n`;

          if (idx === 0) {
            sectionBody += keyTakeaways + statsGrid;
          }

          if (proof && idx === 0) {
            sectionBody += `> **Verified Proof Point:** ${proof}\n\n`;
          }

          if (idx === 1) {
            sectionBody += comparisonGraph + proTipCallout + comparisonTable;
          }

          if (idx === 2) {
            sectionBody += sectionImage;
          }

          sectionBody += `Executing consistently against these parameters ensures measurable performance improvement and sustainable operational ROI.\n`;

          return `${hashes} ${item.text}\n\n${sectionBody}`;
        })
        .join('\n\n');

      const fallbackText = `# ${brief.title}\n\n${heroImage}${directAnswer}${outlineSections}\n\n---\n\n### Primary Action\n\n${brief.cta || 'Get started today'}\n`;
      setDraftMarkdown(appendSchema(fallbackText));
      setSaveStatus('unsaved');
    } finally {
      setIsDrafting(false);
    }
  };

  // Quick insertion helpers for rich visual content
  const handleInsertImage = async () => {
    if (!brief) return;
    setIsSearchingImage(true);
    setCopyToast('Searching live online diagrams with Tavily...');

    try {
      const response = await axiosInstance.post('/seo/briefs/search-images', {
        brief,
        maxResults: 6,
      });

      if (response.data && response.data.success && response.data.data?.images?.length > 0) {
        const images = response.data.data.images;
        setFoundImagesList(images);

        const nextIdx = imageIndex % images.length;
        const selectedUrl = images[nextIdx];
        setImageIndex((prev) => prev + 1);

        const queryUsed = response.data.data.query || 'Tavily Search';
        const caption = `${brief.title} - Visual Architecture (${queryUsed})`;
        const imageMd = `\n\n![${brief.title}](${selectedUrl} "${caption}")\n\n`;

        setDraftMarkdown((prev) => prev + imageMd);
        setSaveStatus('unsaved');
        setCopyToast(`Found & inserted live image via Tavily (${nextIdx + 1}/${images.length})!`);
        setTimeout(() => setCopyToast(null), 3000);
        setIsSearchingImage(false);
        return;
      }
    } catch (err) {
      console.warn('[ContentStudioModal] Tavily image search fallback:', err.message);
    } finally {
      setIsSearchingImage(false);
    }

    // Fallback to topic-curated dynamic image if Tavily returned no images or network failed
    const imageMd = `\n\n${buildDynamicHeroImage(brief)}`;
    setDraftMarkdown((prev) => prev + imageMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted topic-tailored illustration image!');
    setTimeout(() => setCopyToast(null), 2500);
  };

  const handleInsertStatsGrid = () => {
    const statsMd = `\n\n${buildDynamicStatsGrid(brief)}`;
    setDraftMarkdown((prev) => prev + statsMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted dynamic statistics KPI grid!');
    setTimeout(() => setCopyToast(null), 2000);
  };

  const handleInsertGraph = () => {
    const graphMd = `\n\n${buildDynamicGraph(brief)}`;
    setDraftMarkdown((prev) => prev + graphMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted dynamic comparative graph!');
    setTimeout(() => setCopyToast(null), 2000);
  };

  const handleInsertKeyTakeaways = () => {
    const takeawaysMd = `\n\n${buildDynamicKeyTakeaways(brief || { title: 'Article' })}`;
    setDraftMarkdown((prev) => prev + takeawaysMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted Executive Key Takeaways!');
    setTimeout(() => setCopyToast(null), 2000);
  };

  const handleInsertProTip = () => {
    const protipMd = `\n\n${buildDynamicProTip(brief || { title: 'Article' })}`;
    setDraftMarkdown((prev) => prev + protipMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted Expert Pro-Tip callout!');
    setTimeout(() => setCopyToast(null), 2000);
  };

  const handleInsertComparisonTable = () => {
    const tableMd = `\n\n${buildDynamicComparisonTable(brief || { title: 'Article' })}`;
    setDraftMarkdown((prev) => prev + tableMd);
    setSaveStatus('unsaved');
    setCopyToast('Inserted structured comparison table!');
    setTimeout(() => setCopyToast(null), 2000);
  };

  const handleAutoDecorate = () => {
    let md = draftMarkdown || `# ${brief?.title || 'Article'}\n\n`;
    const title = brief?.title || 'Strategic Operations Framework';

    const hasImg = /!\[.*?\]\(.*?\)/.test(md);
    const hasStats = /:::stats|```stats/.test(md);
    const hasGraph = /:::graph|```chart/.test(md);
    const hasTakeaways = />\s*\*\*Key Takeaways/.test(md);
    const hasDirectAnswer = />\s*\*\*Direct Answer/.test(md);
    const hasProTip = />\s*\*\*(?:Pro-Tip|Expert Insight)/.test(md);

    if (!hasImg) {
      const hero = buildDynamicHeroImage(brief || { title });
      if (/^#\s+.+$/m.test(md)) {
        md = md.replace(/^(#\s+.+)$/m, `$1\n\n${hero}`);
      } else {
        md = `${hero}${md}`;
      }
    }
    if (!hasDirectAnswer) {
      const da = buildDynamicDirectAnswer(brief || { title });
      if (/!\[.*?\]\(.*?\)\n\n|#\s+.+\n\n/.test(md)) {
        md = md.replace(/(!\[.*?\]\(.*?\)\n\n|#\s+.+\n\n)/, `$1${da}`);
      } else {
        md = `${da}${md}`;
      }
    }
    if (!hasTakeaways) {
      const ta = buildDynamicKeyTakeaways(brief || { title });
      if (/Direct Answer Definition:[^\n]+\n\n/.test(md)) {
        md = md.replace(/(Direct Answer Definition:[^\n]+\n\n)/, `$1${ta}`);
      } else {
        md = `${ta}${md}`;
      }
    }
    if (!hasStats) {
      const st = buildDynamicStatsGrid(brief || { title });
      if (/^##\s+.+$/m.test(md)) {
        md = md.replace(/^(##\s+.+)$/m, `$1\n\n${st}`);
      } else {
        md += `\n\n${st}`;
      }
    }
    if (!hasGraph) {
      const gr = buildDynamicGraph(brief || { title });
      md += `\n\n${gr}`;
    }
    if (!hasProTip) {
      md += `\n\n${buildDynamicProTip(brief || { title })}`;
    }

    setDraftMarkdown(md);
    setSaveStatus('unsaved');
    setCopyToast('Enriched draft with images, stats, and graph decorations!');
    setTimeout(() => setCopyToast(null), 2500);
  };

  // Insert a citation into draft
  const handleInsertCitation = (point) => {
    const citationMd = `\n\n> **Verified Citation (BR-03):** ${point}\n\n`;
    setDraftMarkdown((prev) => prev + citationMd);
    setSaveStatus('unsaved');
  };

  // Insert an internal link
  const handleInsertLink = (link) => {
    const linkMd = ` [${link.anchorText}](${link.page}) `;
    setDraftMarkdown((prev) => prev + linkMd);
    setSaveStatus('unsaved');
  };

  // Save draft
  const handleSave = () => {
    setSaveStatus('saving');
    const draftData = {
      title: brief.title,
      targetSlug: brief.targetSlug,
      markdown: draftMarkdown,
      wordCount,
      lastSaved: new Date().toISOString(),
      version: '1.0',
    };
    if (onSaveDraft) {
      onSaveDraft(brief.id, draftData);
    }
    setTimeout(() => {
      setSaveStatus('saved');
      setCopyToast('Draft saved successfully!');
      setTimeout(() => setCopyToast(null), 2500);
    }, 400);
  };

  // Copy to clipboard
  const handleCopyMarkdown = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(draftMarkdown);
      setCopyToast('Copied Markdown to clipboard!');
      setTimeout(() => setCopyToast(null), 2500);
    }
  };

  // Download markdown
  const handleDownloadMd = () => {
    const blob = new Blob([draftMarkdown], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const slugName = (brief.targetSlug || '/article').split('/').pop() || 'draft';
    link.href = url;
    link.setAttribute('download', `${slugName}.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Direct handoff to Step 3 Audit
  const handleAuditInStep3 = () => {
    if (onAuditDraft) {
      onAuditDraft(brief, draftMarkdown);
    }
    onClose();
  };

  const isCmsConnected =
    governanceSettings?.connectionStatus === 'connected' &&
    Boolean(governanceSettings?.cmsEndpoint);

  const isBriefApproved = brief?.status === 'approved';

  const isPublished = Boolean(
    publishedData?.publishedAt || brief?.draftArticle?.publishedAt || brief?.lastPublishedAt
  );

  const hasCtaLink = hasLandingPageToken(draftMarkdown);
  const hasSchema = hasJsonLdSchema(draftMarkdown);
  const publishedLandingPages = landingPages.filter((p) => p.publishedUrl);
  const unpublishedLandingPages = landingPages.filter((p) => !p.publishedUrl);
  const linkedLandingPage =
    publishedLandingPages.find((p) => String(p.id) === String(linkedLandingPageId)) || null;
  const landingPageReady = Boolean(linkedLandingPage);
  const previewMarkdown = hasCtaLink
    ? substituteLandingPageUrl(draftMarkdown, linkedLandingPage?.publishedUrl)
    : draftMarkdown;

  const handleSelectLandingPage = async (pageId) => {
    const next = pageId || null;
    const previous = linkedLandingPageId;
    setLinkedLandingPageId(next);
    setShowLandingPageNotice(false);
    if (!onLinkLandingPage) return;
    setIsLinkingLandingPage(true);
    try {
      await onLinkLandingPage(brief.id, next);
    } catch (err) {
      setLinkedLandingPageId(previous);
      setCopyToast('Could not save the landing page selection. Please try again.');
      setTimeout(() => setCopyToast(null), 3000);
    } finally {
      setIsLinkingLandingPage(false);
    }
  };

  const handleInsertLandingPageCta = () => {
    if (hasCtaLink) {
      setDraftMarkdown((prev) => removeLandingPageCta(prev));
      setSaveStatus('unsaved');
      setCopyToast('Removed the landing page CTA link from the article.');
      setTimeout(() => setCopyToast(null), 3000);
      return;
    }
    const ctaMd = `\n\n[Get started today →](${LANDING_PAGE_TOKEN})\n`;
    setDraftMarkdown((prev) => `${(prev || '').replace(/\s+$/, '')}${ctaMd}`);
    setSaveStatus('unsaved');
    setCopyToast('Inserted landing page CTA link. It resolves to the landing page URL on publish.');
    setTimeout(() => setCopyToast(null), 3000);
  };

  const handleGoToLandingPages = () => {
    // Keep the article work before leaving for the Landing Page builder.
    if (onSaveDraft && brief) {
      onSaveDraft(brief.id, {
        title: brief.title,
        targetSlug: brief.targetSlug,
        markdown: draftMarkdown,
        wordCount,
        lastSaved: new Date().toISOString(),
        version: '1.0',
      });
    }
    onOpenLandingPages();
  };

  const handleConfirmPublish = async () => {
    if (!onPublishToCms || !brief) return;
    setIsPublishing(true);
    setPublishError(null);
    setPublishErrorCode(null);

    // Save latest draft state
    const draftData = {
      title: brief.title,
      targetSlug: brief.targetSlug,
      markdown: draftMarkdown,
      wordCount,
      lastSaved: new Date().toISOString(),
      version: '1.0',
    };
    if (onSaveDraft) {
      onSaveDraft(brief.id, draftData);
    }

    try {
      const result = await onPublishToCms(brief, draftMarkdown);
      if (result && (result.success || result.cmsPostId || result.publishedAt)) {
        setPublishedData({
          cmsPostId: result.cmsPostId,
          cmsPostUrl: result.cmsPostUrl,
          cmsStatus: result.cmsStatus,
          publishedAt: result.publishedAt,
          cmsProvider: result.cmsProvider || governanceSettings?.cmsProvider,
        });
        setShowPublishConfirm(false);
        setCopyToast(`Successfully published to ${result.cmsProvider || 'CMS'}!`);
        setTimeout(() => setCopyToast(null), 3500);
      } else {
        setPublishError(result?.error || 'Publishing failed. Please check CMS configuration.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Publishing request failed';
      setPublishError(typeof msg === 'string' ? msg : 'Publishing request failed');
      setPublishErrorCode(err.response?.data?.code || null);
    } finally {
      setIsPublishing(false);
    }
  };

  // Persist the current draft (schema flow saves before leaving the studio).
  const persistDraft = () => {
    if (onSaveDraft && brief) {
      onSaveDraft(brief.id, {
        title: brief.title,
        targetSlug: brief.targetSlug,
        markdown: draftMarkdown,
        wordCount,
        lastSaved: new Date().toISOString(),
        version: '1.0',
      });
    }
  };

  // Intercept Publish when the article has no JSON-LD schema yet.
  const handlePublishClick = () => {
    if (!isBriefApproved && onRequestApproveBrief) {
      onRequestApproveBrief(brief);
      onClose();
      return;
    }
    if (hasCtaLink && !landingPageReady) {
      setShowLandingPageNotice(true);
      return;
    }
    setShowLandingPageNotice(false);
    // Guide the user to add structured data before publishing when missing.
    if (!hasSchema && onStartSchemaFlow) {
      setSchemaAuditHint(false);
      setShowSchemaPrompt(true);
      return;
    }
    setShowPublishConfirm(true);
  };

  // User chose how to seed the schema in the Schema Studio.
  const handleChooseSchemaMode = (mode) => {
    if (mode === 'audit' && !hasAudit) {
      setSchemaAuditHint(true);
      return;
    }
    persistDraft();
    setShowSchemaPrompt(false);
    setSchemaAuditHint(false);
    if (onStartSchemaFlow) {
      onStartSchemaFlow(brief, mode);
    }
  };

  if (!isOpen || !brief) return null;

  return (
    <div className="seo-modal-overlay" onClick={onClose} data-testid="content-studio-modal">
      <div
        className="seo-modal-dialog seo-studio-modal-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="studio-title"
      >
        {/* TOP BAR / STUDIO HEADER */}
        <div className="seo-studio-header">
          <div className="seo-studio-header-left">
            <span className="seo-studio-badge">
              <i className="fas fa-feather-pointed"></i> M06 AI Content Studio
            </span>
            <span className="seo-approved-pill">
              <i className="fas fa-check-circle"></i> Approved Blueprint (FR-M11-002)
            </span>
            <h2 id="studio-title" className="seo-studio-title">
              {brief.title}
            </h2>
            <div className="seo-studio-meta">
              <span>
                <i className="fas fa-link"></i> <code>{brief.targetSlug}</code>
              </span>
              <span>
                <i className="fas fa-bullseye"></i> {brief.searchIntent} intent
              </span>
              <span>
                <i className="fas fa-users"></i> {brief.targetAudience}
              </span>
            </div>
          </div>

          <div className="seo-studio-header-right">
            {copyToast && <div className="seo-studio-toast">{copyToast}</div>}

            <div className="seo-view-mode-group">
              <button
                type="button"
                className={`seo-mode-btn ${viewMode === 'split' ? 'active' : ''}`}
                onClick={() => setViewMode('split')}
                title="Split Editor & Preview"
                data-testid="studio-mode-split"
              >
                <i className="fas fa-columns"></i>
                <span>Split</span>
              </button>
              <button
                type="button"
                className={`seo-mode-btn ${viewMode === 'editor' ? 'active' : ''}`}
                onClick={() => setViewMode('editor')}
                title="Editor Only"
                data-testid="studio-mode-editor"
              >
                <i className="fas fa-pen-to-square"></i>
                <span>Editor</span>
              </button>
              <button
                type="button"
                className={`seo-mode-btn ${viewMode === 'preview' ? 'active' : ''}`}
                onClick={() => setViewMode('preview')}
                title="Formatted Preview"
                data-testid="studio-mode-preview"
              >
                <i className="fas fa-eye"></i>
                <span>Preview</span>
              </button>
            </div>

            <button
              type="button"
              className="seo-studio-close-btn"
              onClick={onClose}
              title="Close Content Studio"
              aria-label="Close"
              data-testid="close-studio-btn"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {publishedData && (
          <div className="seo-studio-published-banner" data-testid="studio-published-banner">
            <div className="seo-studio-published-left">
              <i className="fas fa-circle-check text-green"></i>
              <div>
                <strong>
                  {publishedData.cmsStatus === 'draft'
                    ? 'Staged to CMS (Draft)'
                    : 'Published Live to CMS'}
                  :
                </strong>{' '}
                <span className="text-secondary">
                  via {publishedData.cmsProvider || governanceSettings?.cmsProvider || 'CMS'} on{' '}
                  {new Date(publishedData.publishedAt).toLocaleString()}
                </span>
              </div>
            </div>
            {publishedData.cmsPostUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {publishedData.cmsPostUrl.includes('example.com') && (
                  <span
                    style={{
                      fontSize: '11px',
                      background: '#fef3c7',
                      color: '#92400e',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      border: '1px solid #fde68a',
                      fontWeight: 600,
                    }}
                  >
                    Simulated Sandbox
                  </span>
                )}
                <a
                  href={publishedData.cmsPostUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="seo-published-view-link"
                  data-testid="studio-published-url-link"
                  title={
                    publishedData.cmsPostUrl.includes('example.com')
                      ? 'Simulated URL on sandbox domain (connect your live WordPress URL in Governance to view live posts)'
                      : 'Open live post on CMS'
                  }
                >
                  <i className="fas fa-external-link-alt"></i> View on CMS
                </a>
              </div>
            )}
          </div>
        )}

        {/* WORKSPACE MAIN BODY */}
        <div className="seo-studio-body">
          {/* LEFT/CENTER: EDITOR & PREVIEW PANES */}
          <div className={`seo-studio-canvas ${viewMode}`}>
            {(viewMode === 'split' || viewMode === 'editor') && (
              <div className="seo-studio-editor-pane">
                <div className="seo-studio-pane-header">
                  <div className="seo-pane-title">
                    <i className="fab fa-markdown"></i> Markdown Editor
                  </div>
                  <div className="seo-pane-tools">
                    <button
                      type="button"
                      className="seo-tool-btn"
                      onClick={handleSynthesizeArticle}
                      disabled={isDrafting}
                      title="Synthesize complete draft with Gemini AI"
                      data-testid="studio-ai-synthesize-btn"
                    >
                      {isDrafting ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin"></i>
                          <span>Drafting with AI...</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-robot text-purple"></i>
                          <span>Draft Full Article (AI)</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* QUICK-INSERT AUTHORING TOOLBAR FOR RICH ASSETS */}
                <div className="seo-editor-toolbar" data-testid="studio-editor-toolbar">
                  <span className="seo-toolbar-label">
                    <i className="fas fa-sparkles text-amber"></i> Insert Assets:
                  </span>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertImage}
                    disabled={isSearchingImage}
                    title="Search and insert live online diagrams & illustrations via Tavily"
                    data-testid="studio-insert-image-btn"
                  >
                    {isSearchingImage ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-blue"></i>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-image text-blue"></i>
                        <span>Image (Tavily)</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertStatsGrid}
                    title="Insert 3-metric statistics KPI grid"
                    data-testid="studio-insert-stats-btn"
                  >
                    <i className="fas fa-chart-line text-green"></i>
                    <span>Stats Grid</span>
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertGraph}
                    title="Insert visual comparative performance graph"
                    data-testid="studio-insert-graph-btn"
                  >
                    <i className="fas fa-chart-column text-purple"></i>
                    <span>Graph</span>
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertKeyTakeaways}
                    title="Insert Executive Key Takeaways callout box"
                    data-testid="studio-insert-takeaways-btn"
                  >
                    <i className="fas fa-lightbulb text-amber"></i>
                    <span>Takeaways</span>
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertProTip}
                    title="Insert Expert Pro-Tip callout badge"
                    data-testid="studio-insert-protip-btn"
                  >
                    <i className="fas fa-bolt text-yellow"></i>
                    <span>Pro-Tip</span>
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn"
                    onClick={handleInsertComparisonTable}
                    title="Insert structured comparison data table"
                    data-testid="studio-insert-table-btn"
                  >
                    <i className="fas fa-table text-indigo"></i>
                    <span>Data Table</span>
                  </button>
                  <button
                    type="button"
                    className="seo-toolbar-btn seo-toolbar-btn-highlight"
                    onClick={handleAutoDecorate}
                    title="Auto-enrich article with complete visuals, stats cards, and comparison graph"
                    data-testid="studio-auto-decorate-btn"
                  >
                    <i className="fas fa-wand-magic-sparkles text-purple"></i>
                    <span>Auto-Decorate</span>
                  </button>
                </div>

                <textarea
                  ref={textareaRef}
                  className="seo-studio-textarea"
                  value={draftMarkdown}
                  onChange={handleTextChange}
                  placeholder="# Article Title..."
                  data-testid="studio-markdown-editor"
                  spellCheck="false"
                />
              </div>
            )}

            {(viewMode === 'split' || viewMode === 'preview') && (
              <div className="seo-studio-preview-pane">
                <div className="seo-studio-pane-header">
                  <div className="seo-pane-title">
                    <i className="fas fa-newspaper"></i> Formatted Reader Preview
                  </div>
                  <div className="seo-reading-stats">
                    <span>
                      <i className="fas fa-clock"></i> ~{readingTimeMinutes} min read
                    </span>
                  </div>
                </div>

                <div
                  className="seo-studio-preview-content"
                  data-testid="studio-markdown-preview"
                  dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(previewMarkdown) }}
                />
              </div>
            )}
          </div>

          {/* RIGHT: REAL-TIME ADHERENCE & INTELLIGENCE INSPECTOR */}
          <div className={`seo-studio-inspector ${isInspectorOpen ? 'open' : 'collapsed'}`}>
            <div
              className="seo-inspector-header"
              onClick={() => setIsInspectorOpen((prev) => !prev)}
            >
              <div className="seo-inspector-title">
                <i className="fas fa-compass-drafting text-blue"></i>
                <span>Brief Adherence Inspector</span>
              </div>
              <button
                type="button"
                className="seo-inspector-toggle-btn"
                title={isInspectorOpen ? 'Collapse Inspector' : 'Expand Inspector'}
              >
                <i className={`fas fa-chevron-${isInspectorOpen ? 'right' : 'left'}`}></i>
              </button>
            </div>

            {isInspectorOpen && (
              <div className="seo-inspector-content">
                {/* WORD COUNT PROGRESS */}
                <div className="seo-inspector-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">Target Word Count</span>
                    <span className="seo-card-val">
                      {wordCount} / {targetWordCount} words
                    </span>
                  </div>
                  <div className="seo-progress-track">
                    <div
                      className={`seo-progress-fill ${wordCountPercent >= 90 ? 'complete' : ''}`}
                      style={{ width: `${wordCountPercent}%` }}
                    ></div>
                  </div>
                  <span className="seo-progress-subtext">
                    {wordCountPercent >= 100
                      ? 'Target length achieved!'
                      : `${targetWordCount - wordCount} words remaining`}
                  </span>
                </div>

                {/* SEMANTIC ENTITIES CHECKLIST */}
                <div className="seo-inspector-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">
                      <i className="fas fa-cube text-purple"></i> Semantic Entity Coverage
                    </span>
                    <span className="seo-badge-small">
                      {entityCoverage.filter((e) => e.present).length} / {entityCoverage.length}
                    </span>
                  </div>
                  <p className="seo-inspector-desc">
                    AI engines (Perplexity, ChatGPT) require these named entities:
                  </p>
                  <div className="seo-entity-chips">
                    {entityCoverage.map((ent, idx) => (
                      <span
                        key={idx}
                        className={`seo-entity-chip ${ent.present ? 'present' : 'missing'}`}
                        title={
                          ent.present
                            ? `Present (${ent.occurrences} mentions)`
                            : 'Missing from draft text'
                        }
                      >
                        <i className={`fas fa-${ent.present ? 'check' : 'times'}`}></i>
                        <span>{ent.entity}</span>
                        {ent.present && <span className="seo-count-tag">{ent.occurrences}</span>}
                      </span>
                    ))}
                  </div>
                </div>

                {/* VERIFIED GROUNDED PROOF POINTS */}
                <div className="seo-inspector-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">
                      <i className="fas fa-quote-left text-green"></i> Verified Citations (BR-03)
                    </span>
                  </div>
                  <p className="seo-inspector-desc">
                    Ground draft statements with verified research claims:
                  </p>
                  <ul className="seo-proof-status-list">
                    {proofPointStatus.map((p, idx) => (
                      <li key={idx} className={`seo-proof-status-item ${p.present ? 'cited' : ''}`}>
                        <div className="seo-proof-item-content">
                          <i
                            className={`fas fa-${p.present ? 'check-circle text-green' : 'circle-exclamation text-amber'}`}
                          ></i>
                          <span>{p.point}</span>
                        </div>
                        {!p.present && (
                          <button
                            type="button"
                            className="seo-insert-btn"
                            onClick={() => handleInsertCitation(p.point)}
                            title="Insert citation block into draft"
                          >
                            + Insert
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* INTERNAL LINK PLACEMENTS */}
                <div className="seo-inspector-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">
                      <i className="fas fa-link text-blue"></i> Internal Links
                    </span>
                  </div>
                  <ul className="seo-links-status-list">
                    {internalLinkStatus.map((l, idx) => (
                      <li key={idx} className={`seo-link-status-item ${l.present ? 'linked' : ''}`}>
                        <div className="seo-link-item-top">
                          <code>{l.page}</code>
                          {l.present ? (
                            <span className="text-green">
                              <i className="fas fa-check"></i> Linked
                            </span>
                          ) : (
                            <button
                              type="button"
                              className="seo-insert-btn"
                              onClick={() => handleInsertLink(l)}
                              title="Insert link into draft"
                            >
                              + Insert
                            </button>
                          )}
                        </div>
                        <div className="seo-link-anchor">"{l.anchorText}"</div>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA VERIFICATION */}
                <div className="seo-inspector-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">Primary Call to Action</span>
                    <span
                      className={`seo-status-mini-tag ${ctaCheck.present ? 'success' : 'warning'}`}
                    >
                      {ctaCheck.present ? 'Included' : 'Needs Insertion'}
                    </span>
                  </div>
                  <p className="seo-cta-snippet">{ctaCheck.cta}</p>
                </div>

                {/* LANDING PAGE CTA (WF-04 / FR-M12-006) */}
                <div className="seo-inspector-card" data-testid="studio-landing-page-card">
                  <div className="seo-inspector-card-header">
                    <span className="seo-card-label">
                      <i className="fas fa-bullseye text-blue"></i> Landing Page CTA
                    </span>
                    <span
                      className={`seo-status-mini-tag ${
                        hasCtaLink && landingPageReady ? 'success' : 'warning'
                      }`}
                    >
                      {!hasCtaLink
                        ? 'No CTA link'
                        : landingPageReady
                          ? 'Linked'
                          : 'Landing page needed'}
                    </span>
                  </div>
                  {landingPagesStatus === 'disabled' ? (
                    <p className="seo-inspector-desc">Landing pages are not enabled.</p>
                  ) : (
                    <>
                      <label className="seo-inspector-desc" htmlFor="studio-landing-page-select">
                        Approved, published landing page
                      </label>
                      <select
                        id="studio-landing-page-select"
                        className="seo-quick-connect-select"
                        value={linkedLandingPageId || ''}
                        disabled={landingPagesStatus === 'loading' || isLinkingLandingPage}
                        onChange={(e) => handleSelectLandingPage(e.target.value)}
                        data-testid="studio-landing-page-select"
                      >
                        <option value="">
                          {landingPagesStatus === 'loading' ? 'Loading…' : 'None selected'}
                        </option>
                        {publishedLandingPages.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                        {unpublishedLandingPages.map((p) => (
                          <option key={p.id} value={p.id} disabled>
                            {p.name} (approved, not published yet)
                          </option>
                        ))}
                        {linkedLandingPageId &&
                          !linkedLandingPage &&
                          !landingPages.some(
                            (p) => String(p.id) === String(linkedLandingPageId)
                          ) && (
                            <option value={linkedLandingPageId}>
                              (selected page is not published)
                            </option>
                          )}
                      </select>
                      {landingPagesStatus === 'ready' && publishedLandingPages.length === 0 && (
                        <p className="seo-inspector-desc" data-testid="studio-landing-page-hint">
                          {unpublishedLandingPages.length > 0
                            ? 'Your approved landing page is not published yet. Open the Landing Page builder and click "Publish to CMS" to get its URL.'
                            : 'No approved landing page yet. Build and approve one in the Landing Page builder, then publish it.'}
                        </p>
                      )}
                      {landingPagesStatus === 'error' && (
                        <p className="seo-inspector-desc">Could not load landing pages.</p>
                      )}
                      {linkedLandingPage && (
                        <p className="seo-cta-snippet" data-testid="studio-landing-page-url">
                          {linkedLandingPage.publishedUrl}
                        </p>
                      )}
                    </>
                  )}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="seo-insert-btn"
                      onClick={handleInsertLandingPageCta}
                      data-testid="studio-insert-landing-cta-btn"
                      title={
                        hasCtaLink
                          ? 'Remove the landing page CTA link from the article'
                          : 'Insert a CTA link that resolves to the selected landing page on publish'
                      }
                    >
                      {hasCtaLink ? 'Remove CTA link' : '+ Insert CTA link'}
                    </button>
                    <button
                      type="button"
                      className="seo-insert-btn"
                      onClick={handleGoToLandingPages}
                      data-testid="studio-open-landing-pages-btn"
                    >
                      Landing Page
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {showLandingPageNotice && (
          <div
            className="seo-confirm-error"
            role="alert"
            data-testid="studio-landing-page-notice"
            style={{ margin: '0 16px 8px', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <i className="fas fa-circle-exclamation"></i>
            <span style={{ flex: 1 }}>
              This article has a landing page CTA link, but no approved and published landing page
              is selected. Select one in the Landing Page CTA panel, or create and publish one
              first.
            </span>
            <button
              type="button"
              className="seo-btn seo-btn-primary seo-btn-sm"
              onClick={handleGoToLandingPages}
              data-testid="studio-landing-page-notice-btn"
            >
              Landing Page
            </button>
            <button
              type="button"
              className="seo-btn seo-btn-secondary seo-btn-sm"
              onClick={() => setShowLandingPageNotice(false)}
              aria-label="Dismiss"
            >
              &times;
            </button>
          </div>
        )}

        {/* BOTTOM ACTION BAR */}
        <div className="seo-studio-footer">
          <div className="seo-footer-left">
            <span className={`seo-save-indicator ${saveStatus}`}>
              <i
                className={`fas fa-${saveStatus === 'saved' ? 'check-double' : saveStatus === 'saving' ? 'spinner fa-spin' : 'circle-dot'}`}
              ></i>
              <span>
                {saveStatus === 'saved'
                  ? 'All changes saved'
                  : saveStatus === 'saving'
                    ? 'Saving draft...'
                    : 'Unsaved changes'}
              </span>
            </span>
          </div>

          <div className="seo-footer-actions">
            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              onClick={handleCopyMarkdown}
              title="Copy raw markdown to clipboard"
            >
              <i className="fas fa-copy"></i>
              <span>Copy Markdown</span>
            </button>

            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              onClick={handleDownloadMd}
              title="Download as .md file"
            >
              <i className="fas fa-download"></i>
              <span>Export .md</span>
            </button>

            <button
              type="button"
              className="seo-btn seo-btn-secondary"
              onClick={handleSave}
              data-testid="studio-save-draft-btn"
            >
              <i className="fas fa-floppy-disk"></i>
              <span>Save Draft</span>
            </button>

            <button
              type="button"
              className="seo-btn seo-btn-primary seo-btn-audit-pulse"
              onClick={handleAuditInStep3}
              title="Send drafted article to Step 3 for On-Page & AIEO Audit"
              data-testid="studio-audit-step3-btn"
            >
              <i className="fas fa-stethoscope"></i>
              <span>Audit Draft in Step 3</span>
            </button>

            {onPublishToCms && (
              <button
                type="button"
                className={`seo-btn ${isPublished ? 'seo-btn-published-done' : 'seo-btn-publish'}`}
                onClick={handlePublishClick}
                disabled={isPublishing || (!isBriefApproved && !onRequestApproveBrief)}
                title={
                  !isBriefApproved
                    ? onRequestApproveBrief
                      ? 'Brief must be approved before publishing — click to approve in Content Briefs'
                      : 'Brief must be approved before publishing to CMS (BR-05)'
                    : isCmsConnected
                      ? 'Publish or stage this drafted article to connected CMS (FR-M11-009)'
                      : 'Connect your CMS in Publishing Governance to enable publication'
                }
                data-testid="studio-publish-cms-btn"
              >
                {isPublishing ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Publishing...</span>
                  </>
                ) : isPublished ? (
                  <>
                    <i className="fas fa-cloud-arrow-up text-green"></i>
                    <span>Republish to CMS</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-cloud-arrow-up text-purple"></i>
                    <span>Publish to CMS</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* STRUCTURED DATA (JSON-LD) MISSING PROMPT BEFORE PUBLISH */}
        {showSchemaPrompt && (
          <div className="seo-publish-confirm-overlay" data-testid="schema-prompt-dialog">
            <div className="seo-publish-confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="seo-publish-confirm-header">
                <h4>
                  <i className="fas fa-code text-purple"></i> Add Structured Data (JSON-LD) First?
                </h4>
                <button
                  type="button"
                  className="seo-confirm-close-btn"
                  onClick={() => {
                    setShowSchemaPrompt(false);
                    setSchemaAuditHint(false);
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>

              <div className="seo-publish-confirm-body">
                <p className="seo-confirm-subtitle">
                  This article has no JSON-LD structured data yet. Schema.org markup helps search
                  engines show rich snippets and lets AI answer engines (ChatGPT, Perplexity, Google
                  AI Overviews) accurately ground and cite your content. Adding it before publishing
                  significantly improves visibility.
                </p>

                <p className="seo-inspector-desc" style={{ marginBottom: '8px' }}>
                  Choose how you'd like to generate the structured data:
                </p>

                <div
                  style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}
                >
                  <button
                    type="button"
                    className="seo-btn seo-btn-secondary"
                    onClick={() => handleChooseSchemaMode('manual')}
                    data-testid="schema-prompt-manual-btn"
                    title="Open the Schema Studio and build the markup manually"
                  >
                    <i className="fas fa-pen"></i>
                    <span>Manual</span>
                  </button>

                  <button
                    type="button"
                    className={`seo-btn seo-btn-secondary ${hasAudit ? '' : 'seo-btn-disabled-hint'}`}
                    aria-disabled={!hasAudit}
                    onClick={() => handleChooseSchemaMode('audit')}
                    data-testid="schema-prompt-audit-btn"
                    title={
                      hasAudit
                        ? 'Seed the schema from your latest On-Page audit'
                        : 'Run an On-Page & AIEO audit first to use this option'
                    }
                  >
                    <i className="fas fa-file-shield"></i>
                    <span>From Audited Page</span>
                  </button>

                  <button
                    type="button"
                    className="seo-btn seo-btn-primary"
                    onClick={() => handleChooseSchemaMode('ai')}
                    data-testid="schema-prompt-ai-btn"
                    title="Let AI generate tailored structured data"
                  >
                    <i className="fas fa-wand-magic-sparkles"></i>
                    <span>AI Generate</span>
                  </button>
                </div>

                {schemaAuditHint && (
                  <div className="seo-confirm-error" data-testid="schema-prompt-audit-hint">
                    <i className="fas fa-circle-exclamation"></i>
                    <span>
                      You should run an On-Page &amp; AIEO audit first. Once a page has been
                      audited, you can seed the structured data from it.
                    </span>
                  </div>
                )}
              </div>

              <div className="seo-publish-confirm-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-text"
                  onClick={() => handleChooseSchemaMode('manual')}
                  data-testid="schema-prompt-details-btn"
                  title="Open the full Schema Studio to review and edit all fields"
                >
                  <i className="fas fa-sliders"></i>
                  <span>Details</span>
                </button>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => {
                    setShowSchemaPrompt(false);
                    setSchemaAuditHint(false);
                    setShowPublishConfirm(true);
                  }}
                  data-testid="schema-prompt-skip-btn"
                >
                  Publish without schema
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PUBLISH TO CMS CONFIRMATION DIALOG (FR-M11-009) */}
        {showPublishConfirm && (
          <div className="seo-publish-confirm-overlay" data-testid="publish-confirm-dialog">
            <div className="seo-publish-confirm-card" onClick={(e) => e.stopPropagation()}>
              <div className="seo-publish-confirm-header">
                <h4>
                  <i className="fas fa-shield-halved text-purple"></i> Confirm CMS Publication
                  Governance (FR-M11-009)
                </h4>
                <button
                  type="button"
                  className="seo-confirm-close-btn"
                  onClick={() => {
                    setShowPublishConfirm(false);
                    setPublishError(null);
                  }}
                >
                  &times;
                </button>
              </div>

              <div className="seo-publish-confirm-body">
                <p className="seo-confirm-subtitle">
                  Verify publishing guardrails and target destination before dispatching:
                </p>

                <div className="seo-confirm-checklist">
                  <div
                    className={`seo-confirm-check-item ${isBriefApproved ? 'passed' : 'failed'}`}
                  >
                    <i
                      className={`fas fa-${isBriefApproved ? 'check-circle text-green' : 'circle-xmark text-red'}`}
                    ></i>
                    <div>
                      <strong>Editorial Approval Gate (BR-05):</strong>
                      <span>
                        {' '}
                        {isBriefApproved
                          ? brief.approvedBy
                            ? `Approved by ${brief.approvedBy}`
                            : 'Approved Blueprint'
                          : 'Pending Approval (Must be approved prior to publishing)'}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`seo-confirm-check-item ${wordCount >= 100 ? 'passed' : 'warning'}`}
                  >
                    <i
                      className={`fas fa-${wordCount >= 100 ? 'check-circle text-green' : 'triangle-exclamation text-amber'}`}
                    ></i>
                    <div>
                      <strong>Content Readiness:</strong>
                      <span>
                        {' '}
                        {wordCount} words drafted ({wordCountPercent}% of target {targetWordCount})
                      </span>
                    </div>
                  </div>

                  <div className={`seo-confirm-check-item ${isCmsConnected ? 'passed' : 'failed'}`}>
                    <i
                      className={`fas fa-${isCmsConnected ? 'check-circle text-green' : 'circle-xmark text-red'}`}
                    ></i>
                    <div style={{ flex: 1 }}>
                      <strong>Connected Destination:</strong>
                      <span>
                        {' '}
                        {isCmsConnected
                          ? `${governanceSettings.cmsProvider || 'WordPress'} (${governanceSettings.cmsEndpoint})`
                          : 'No CMS connected. Please configure in Publishing Governance.'}
                      </span>
                      {isCmsConnected &&
                        governanceSettings?.cmsEndpoint?.includes('example.com') && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginLeft: '8px',
                              fontSize: '11px',
                              padding: '1px 6px',
                              borderRadius: '4px',
                              background: '#fef3c7',
                              color: '#92400e',
                              border: '1px solid #fde68a',
                              fontWeight: 600,
                            }}
                          >
                            Simulation Sandbox
                          </span>
                        )}
                    </div>
                    {isCmsConnected && onOpenGovernance && (
                      <button
                        type="button"
                        className="seo-btn-inline-change"
                        onClick={() => {
                          setShowPublishConfirm(false);
                          onOpenGovernance();
                        }}
                        title="Change CMS or governance settings"
                      >
                        Change
                      </button>
                    )}
                  </div>

                  {isCmsConnected && governanceSettings?.cmsEndpoint?.includes('example.com') && (
                    <div
                      style={{
                        padding: '8px 12px',
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#1e40af',
                        marginBottom: '8px',
                      }}
                    >
                      <i className="fas fa-info-circle" style={{ marginRight: '6px' }}></i>
                      <strong>Sandbox Mode:</strong> <code>example.com</code> is a simulated demo
                      domain to test editorial gates safely. To publish to your real WordPress site,
                      click <strong>Change</strong> above and enter your actual website URL (e.g.{' '}
                      <code>https://your-site.com/wp-json/</code>) and Application Password.
                    </div>
                  )}

                  {!isCmsConnected && (
                    <div className="seo-quick-connect-box" data-testid="quick-connect-cms-box">
                      <div className="seo-quick-connect-header">
                        <i className="fas fa-plug text-purple"></i>
                        <span>Quick Connect CMS Destination</span>
                      </div>
                      <p className="seo-quick-connect-desc">
                        Connect your live CMS endpoint, or use a simulated staging environment with
                        1 click to test publishing:
                      </p>

                      <div className="seo-quick-connect-controls">
                        <select
                          className="seo-quick-connect-select"
                          value={quickCmsProvider}
                          onChange={(e) => setQuickCmsProvider(e.target.value)}
                          data-testid="quick-connect-provider-select"
                        >
                          <option value="WordPress">WordPress</option>
                          <option value="Webflow">Webflow</option>
                          <option value="Ghost">Ghost</option>
                          <option value="Generic Headless">Headless</option>
                        </select>
                        <input
                          type="text"
                          className="seo-quick-connect-input"
                          placeholder="e.g. https://your-wordpress-site.com/wp-json/"
                          value={quickCmsEndpoint}
                          onChange={(e) => setQuickCmsEndpoint(e.target.value)}
                          data-testid="quick-connect-endpoint-input"
                        />
                        <button
                          type="button"
                          className="seo-btn seo-btn-primary seo-btn-sm"
                          onClick={handleSaveQuickEndpoint}
                          disabled={!quickCmsEndpoint.trim()}
                          data-testid="quick-connect-save-btn"
                        >
                          Connect
                        </button>
                      </div>

                      <div className="seo-quick-connect-actions">
                        <button
                          type="button"
                          className="seo-btn seo-btn-secondary seo-btn-xs"
                          onClick={handleConnectDemoCms}
                          data-testid="quick-connect-demo-btn"
                          title="Connects a simulated staging endpoint (https://staging.example.com/wp-json/)"
                        >
                          <i className="fas fa-wand-magic-sparkles text-purple"></i>
                          <span>Use Demo Staging CMS</span>
                        </button>
                        {onOpenGovernance && (
                          <button
                            type="button"
                            className="seo-btn seo-btn-secondary seo-btn-xs"
                            onClick={() => {
                              setShowPublishConfirm(false);
                              onOpenGovernance();
                            }}
                          >
                            <i className="fas fa-sliders"></i>
                            <span>All Governance Settings</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {hasCtaLink && (
                    <div
                      className={`seo-confirm-check-item ${landingPageReady ? 'passed' : 'failed'}`}
                    >
                      <i
                        className={`fas fa-${landingPageReady ? 'check-circle text-green' : 'circle-xmark text-red'}`}
                      ></i>
                      <div>
                        <strong>Landing Page CTA:</strong>
                        <span>
                          {' '}
                          {landingPageReady
                            ? `Links to "${linkedLandingPage.name}" (${linkedLandingPage.publishedUrl})`
                            : 'No approved, published landing page selected'}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="seo-confirm-check-item passed">
                    <i className="fas fa-shield text-blue"></i>
                    <div>
                      <strong>Publishing Mode:</strong>
                      <span>
                        {governanceSettings.autoStageToCms
                          ? ' Staging as Draft first (Rollback supported)'
                          : ' Direct Publication'}
                      </span>
                    </div>
                  </div>
                </div>

                {publishError && (
                  <div className="seo-confirm-error" data-testid="publish-error-msg">
                    <i className="fas fa-circle-exclamation"></i>
                    <span>{publishError}</span>
                    {publishErrorCode === 'LANDING_PAGE_REQUIRED' && (
                      <button
                        type="button"
                        className="seo-btn seo-btn-primary seo-btn-sm"
                        onClick={handleGoToLandingPages}
                        data-testid="publish-error-landing-page-btn"
                      >
                        Landing Page
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="seo-publish-confirm-footer">
                <button
                  type="button"
                  className="seo-btn seo-btn-secondary"
                  onClick={() => {
                    setShowPublishConfirm(false);
                    setPublishError(null);
                  }}
                  disabled={isPublishing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="seo-btn seo-btn-primary"
                  onClick={handleConfirmPublish}
                  disabled={
                    isPublishing ||
                    !isCmsConnected ||
                    !isBriefApproved ||
                    (hasCtaLink && !landingPageReady)
                  }
                  data-testid="confirm-publish-btn"
                >
                  {isPublishing ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>Publishing to CMS...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-cloud-arrow-up"></i>
                      <span>Confirm &amp; Publish</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
